import {
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	InputLabel,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Switch,
	TextField,
	Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { showError, showSuccess } from '../../../actions/misc/misc.actions'
import { DeletableChip } from '../../Misc/DeletableChip'
import { api } from '../../../services/api'

const pages = [
	'ScheduleTraining',
	'StartCheckIn',
	'WelcomeDay',
	'ScheduleDaycare',
	'ScheduleBoarding',
	'BookGrooming',
	'Memberships',
	'Gallery',
	'Pets',
	'Bank'
]
const audience_options = ['All']

const useStyles = makeStyles((theme) => ({
	root2: {
		width: 45,
		height: 26,
		padding: 0,
		margin: theme.spacing(1)
	},
	switchBase: {
		padding: 1,
		'&$checked': {
			transform: 'translateX(16px)',
			color: theme.palette.common.white,
			'& + $track': {
				backgroundColor: '#52d869',
				opacity: 1,
				border: 'none'
			}
		},
		'&$focusVisible $thumb': {
			color: '#52d869',
			border: '6px solid #fff'
		}
	},
	thumb: {
		width: 24,
		height: 24
	},
	track: {
		borderRadius: 26 / 2,
		border: `1px solid ${theme.palette.grey[400]}`,
		backgroundColor: theme.palette.grey[50],
		opacity: 1,
		transition: theme.transitions.create(['background-color', 'border'])
	},
	checked: {},
	bottomBar: {
		position: 'fixed',
		zIndex: 1000,
		bottom: 0,
		width: '100%',
		height: 60,
		backgroundColor: '#FFFFFF',
		border: '1px solid #D0CFD2',
		borderLeftWidth: 0,
		borderTopRightRadius: 6,
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		padding: 20
	}
}))

const emptyNote = {
	message: '',
	buttonText: '',
	location: 'Client App',
	page: 'StartCheckIn',
	audiences: ['All'],
	active: false,
	link: ''
}

const FacilityDashNotifications = ({ facilityId }) => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const [message, setMessage] = useState('')
	const [buttonText, setButtonText] = useState('')
	const [location, setLocation] = useState('Client App')
	const [page, setPage] = useState('StartCheckIn')
	const [link, setLink] = useState('')
	const [audiences, setAudiences] = useState(['All'])
	const [loading, setLoading] = useState(false)
	const [changed, setChanged] = useState(false)
	const [active, setActive] = useState(false)
	const [initialNote, setInitialNote] = useState(null)
	const [loaded, setLoaded] = useState(false)

	const setNotification = (note) => {
		const { active, audiences, buttonText, location, message, page, link } = note
		setActive(!!active)
		setAudiences(audiences || ['All'])
		setButtonText(buttonText || '')
		setLocation(location || 'Client App')
		setMessage(message || '')
		setPage(page || 'StartCheckIn')
		setLink(link || '')
	}

	useEffect(() => {
		if (message && buttonText && audiences && loaded && (page || link)) {
			setChanged(true)
		} else {
			setChanged(false)
		}
	}, [message, buttonText, audiences, location, link, active, page, loaded])

	useEffect(() => {
		const fetchNotifications = async () => {
			try {
				if (!facilityId) return

				const { data } = await api.get(`/facility/${facilityId}/dashNotifications`)
				if (data) {
					setNotification(data)
					setInitialNote(data)
				} else {
					setNotification(emptyNote)
					setInitialNote(null)
				}
				setLoaded(true)
			} catch (e) {
				console.log('err...', e)
				setLoaded(true)
			}
		}

		setLoaded(false)
		setChanged(false)
		fetchNotifications()
	}, [facilityId])

	const sendNotification = async () => {
		try {
			if (!facilityId) return

			setLoading(true)
			const obj = {
				buttonText,
				message,
				location,
				audiences,
				active
			}
			if (location === 'Client App') {
				obj.page = page
			} else {
				obj.link = link
			}

			const { data } = await api.post(`/facility/${facilityId}/dashNotifications`, obj)
			setInitialNote(data)
			dispatch(showSuccess('Saved!'))
			setLoading(false)
			setTimeout(() => {
				setChanged(false)
			}, 300)
		} catch (e) {
			setLoading(false)
			dispatch(showError('Error sending notification'))
			console.log('err...', e)
		}
	}

	const revert = () => {
		if (initialNote) {
			setNotification(initialNote)
		} else {
			setNotification(emptyNote)
		}
		setTimeout(() => {
			setChanged(false)
		}, 500)
	}

	return (
		<div>
			<Paper style={{ padding: 24, marginBottom: 24 }}>
				<Typography variant="h5" style={{ fontSize: 16, marginBottom: 16 }}>
					Set Up A Notification
				</Typography>
				<TextField
					label="Message"
					value={message}
					style={{ width: '100%', marginBottom: 16 }}
					variant="filled"
					onChange={(e) => setMessage(e.target.value)}
					error={message.length > 50}
					helperText={`${message.length} / 50 Max Characters`}
				/>
				<TextField
					label="Button Text"
					value={buttonText}
					style={{ width: '100%', marginBottom: 16 }}
					variant="filled"
					onChange={(e) => setButtonText(e.target.value)}
					error={buttonText.length > 19}
					helperText={`${buttonText.length} / 19 Max Characters`}
				/>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						marginBottom: 16
					}}>
					<TextField
						select
						label="Location"
						value={location}
						style={{ width: '48%', marginBottom: 16 }}
						variant="filled"
						onChange={(e) => setLocation(e.target.value)}>
						<MenuItem value="Client App">Client App</MenuItem>
						<MenuItem value="Web Link">Web Link</MenuItem>
					</TextField>
					{location === 'Client App' ? (
						<TextField
							select
							label="Page"
							value={page}
							style={{ width: '48%', marginBottom: 16 }}
							variant="filled"
							onChange={(e) => setPage(e.target.value)}>
							{pages.map((p) => (
								<MenuItem key={p} value={p}>
									{p}
								</MenuItem>
							))}
						</TextField>
					) : (
						<TextField
							label="URL"
							value={link}
							style={{ width: '48%', marginBottom: 16 }}
							variant="filled"
							onChange={(e) => setLink(e.target.value)}
						/>
					)}
				</div>
				<FormControl style={{ width: '100%' }}>
					<InputLabel id="facility-dash-audience-label">Audience</InputLabel>
					<Select
						labelId="facility-dash-audience-label"
						variant="filled"
						value={audiences}
						label="Audience"
						style={{ width: '100%' }}
						multiple
						renderValue={(selected) => (
							<div>
								{selected.map((value) => (
									<DeletableChip key={value} label={value} />
								))}
							</div>
						)}
						onChange={(e) => setAudiences(e.target.value)}>
						{audience_options.map((aud) => (
							<MenuItem key={aud} value={aud}>
								<Checkbox checked={audiences.includes(aud)} />
								<ListItemText primary={aud} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<div
					style={{
						paddingTop: 24,
						width: '100%'
					}}>
					<div
						style={{
							padding: 16,
							borderRadius: 6,
							display: 'flex',
							backgroundColor: '#F9F9F9',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'center',
							width: '50%'
						}}>
						<Typography variant="body2">Enable Content Block</Typography>
						<Switch
							disableRipple
							checked={active}
							onChange={() => setActive(!active)}
							classes={{
								root: classes.root2,
								switchBase: classes.switchBase,
								thumb: classes.thumb,
								track: classes.track,
								checked: classes.checked
							}}
						/>
					</div>
				</div>
			</Paper>
			{changed ? (
				<div className={classes.bottomBar}>
					<Button
						style={{
							fontWeight: 800,
							letterSpacing: 1.25,
							fontSize: '14px',
							lineHeight: '22px',
							backgroundColor: '#6B4AD6',
							borderRadius: 19,
							height: 38,
							color: '#FFFFFF',
							textTransform: 'uppercase',
							width: 200,
							padding: '6px 16px'
						}}
						variant="contained"
						onClick={sendNotification}
						disabled={!message || !buttonText || !audiences.length || loading}>
						{loading ? <CircularProgress size={22} /> : 'Save Changes'}
					</Button>
					<Button style={{ marginLeft: 16 }} onClick={revert} variant="outlined" size="large">
						Cancel
					</Button>
				</div>
			) : null}
		</div>
	)
}

export default FacilityDashNotifications
